/** Das Regal — Einstieg in die eigene Bibliothek.
 *
 *  Die Bretter lassen sich unterschiedlich füllen: nach Status, nach Lesejahr
 *  (die Jahresringe), nach Reihen mit Lückenanzeige oder nach den eigenen
 *  Listen. Dazu Suche und Filter. Sortierung und Filter bleiben in der Sitzung
 *  erhalten, weil man sonst nach jedem Buch von vorn anfängt.
 */

import { useMemo, useState } from "react";
import type { Book } from "../../../shared/types";
import { OWNER_NAME } from "../../../shared/types";
import { foldAccents } from "../../../shared/normalize";
import { Regal, type BrettDaten } from "../components/Regal";
import { Detail } from "../components/Detail";
import { useStore } from "../lib/store";

type Ordnung = "status" | "jahr" | "reihe" | "liste" | "titel";

const ORDNUNGEN: { key: Ordnung; name: string }[] = [
  { key: "status", name: "nach Status" },
  { key: "jahr", name: "nach Lesejahr" },
  { key: "reihe", name: "nach Reihen" },
  { key: "liste", name: "nach Listen" },
  { key: "titel", name: "alphabetisch" },
];

export function RegalSeite() {
  const { owner, books, lists, series, laedt, fehler, neuLaden } = useStore();
  const [ordnung, setOrdnung] = useState<Ordnung>("status");
  const [suche, setSuche] = useState("");
  const [nurFav, setNurFav] = useState(false);
  const [nurBeide, setNurBeide] = useState(false);
  const [tag, setTag] = useState<string | null>(null);
  const [offen, setOffen] = useState<Book | null>(null);

  const alleTags = useMemo(() => {
    const zaehler = new Map<string, number>();
    for (const b of books)
      for (const t of b.tags || [])
        zaehler.set(t.name, (zaehler.get(t.name) || 0) + 1);
    return [...zaehler.entries()].sort((a, b) => b[1] - a[1]).slice(0, 18);
  }, [books]);

  const gefiltert = useMemo(() => {
    const q = foldAccents(suche.trim().toLowerCase());
    return books.filter((b) => {
      if (nurFav && !b.fav) return false;
      if (nurBeide && !b.also) return false;
      if (tag && !(b.tags || []).some((t) => t.name === tag)) return false;
      if (!q) return true;
      const heu = foldAccents(
        `${b.title} ${b.author} ${b.series ?? ""}`.toLowerCase(),
      );
      return heu.includes(q);
    });
  }, [books, suche, nurFav, nurBeide, tag]);

  const bretter = useMemo<BrettDaten[]>(() => {
    const nachTitel = (a: Book, b: Book) =>
      a.title.localeCompare(b.title, "de");

    if (ordnung === "status") {
      const fav = gefiltert.filter((b) => b.fav).sort(nachTitel);
      const rest = (s: Book["status"]) =>
        gefiltert.filter((b) => b.status === s && !b.fav).sort(nachTitel);
      return [
        { titel: "Lieblingsbücher", books: fav },
        { titel: "Gerade offen", books: rest("reading") },
        { titel: "Gelesen", books: rest("read") },
        { titel: "Auf der Liste", books: rest("want") },
        {
          titel: "Abgebrochen",
          bemerkung: "kein Urteil, nur ein Hinweis",
          books: rest("dnf"),
        },
      ];
    }

    if (ordnung === "jahr") {
      const jahre = new Map<string, Book[]>();
      const ohne: Book[] = [];
      for (const b of gefiltert) {
        if (!b.read_year) ohne.push(b);
        else {
          const arr = jahre.get(b.read_year);
          if (arr) arr.push(b);
          else jahre.set(b.read_year, [b]);
        }
      }
      const sortiert = [...jahre.entries()].sort((a, b) =>
        b[0].localeCompare(a[0]),
      );
      return [
        ...sortiert.map(([jahr, liste]) => ({
          titel: jahr,
          books: liste.sort(nachTitel),
        })),
        // Die Platzhalter-Daten aus dem Export dürfen nie als Lesejahr auftauchen
        {
          titel: "Ohne Datum",
          bemerkung: "im Export nicht gepflegt",
          books: ohne.sort(nachTitel),
        },
      ];
    }

    if (ordnung === "reihe") {
      const inReihe = new Set<number>();
      // Über die IDs auf den AKTUELLEN Bestand abbilden: `series` kommt vom
      // Server und weiß nichts von Änderungen, die seither im Detail passiert
      // sind (Liebling gesetzt, Status geändert).
      const nachId = new Map(gefiltert.map((b) => [b.id, b]));
      const reihen = series
        .map((s) => {
          const teil = s.books
            .map((b) => nachId.get(b.id))
            .filter((b): b is Book => !!b);
          teil.forEach((b) => inReihe.add(b.id));
          return {
            titel: s.name,
            bemerkung: s.gaps.length
              ? `es fehlt Band ${s.gaps.join(", ")}`
              : undefined,
            books: teil,
          };
        })
        .filter((s) => s.books.length > 0);
      return [
        ...reihen,
        {
          titel: "Einzelbände",
          books: gefiltert.filter((b) => !inReihe.has(b.id)).sort(nachTitel),
        },
      ];
    }

    if (ordnung === "liste") {
      const inListe = new Set<number>();
      const nachListe = lists.map((l) => {
        const teil = gefiltert.filter((b) => (b.lists || []).includes(l.id));
        teil.forEach((b) => inListe.add(b.id));
        return { titel: l.name, books: teil.sort(nachTitel) };
      });
      return [
        ...nachListe.filter((l) => l.books.length),
        {
          titel: "In keiner Liste",
          books: gefiltert.filter((b) => !inListe.has(b.id)).sort(nachTitel),
        },
      ];
    }

    return [{ titel: "Alle Bände", books: [...gefiltert].sort(nachTitel) }];
  }, [ordnung, gefiltert, series, lists]);

  const mitCover = gefiltert.filter((b) => b.cover).length;

  return (
    <>
      <section style={{ maxWidth: "42ch" }}>
        <h2 className="titelzeile">{OWNER_NAME[owner]}s Bestand</h2>
        <p className="lauftext">
          Jeder Band steht mit seinem echten Umschlag im Saal. Zeigen zeigt
          Titel und Autor, ein Klick schlägt das Buch auf — dort stehen Notizen,
          Bewertung und Schlagworte.
        </p>
        <p className="klein" style={{ marginTop: 18 }}>
          {gefiltert.length} von {books.length} Bänden · {mitCover} mit Umschlag
          {laedt ? " · lädt …" : ""}
        </p>
      </section>

      {fehler && (
        <div
          className="karte"
          style={{ borderLeft: "3px solid var(--bad)", marginTop: 20 }}
        >
          <strong>Bestand nicht ladbar.</strong> {fehler}{" "}
          <button
            className="knopf"
            style={{ marginLeft: 8 }}
            onClick={() => void neuLaden()}
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "center",
          margin: "30px 0 6px",
        }}
      >
        <input
          style={{ width: "min(280px, 60vw)" }}
          placeholder="Suchen — Titel, Autor, Reihe"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
        />
        {ORDNUNGEN.map((o) => (
          <button
            key={o.key}
            className={`chip${ordnung === o.key ? " an" : ""}`}
            onClick={() => setOrdnung(o.key)}
          >
            {o.name}
          </button>
        ))}
        <button
          className={`chip${nurFav ? " an" : ""}`}
          onClick={() => setNurFav((v) => !v)}
        >
          ★ nur Lieblinge
        </button>
        <button
          className={`chip${nurBeide ? " an" : ""}`}
          onClick={() => setNurBeide((v) => !v)}
        >
          ◆ auch nebenan
        </button>
      </div>

      {alleTags.length > 0 && (
        <div
          style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}
        >
          {alleTags.map(([name, n]) => (
            <button
              key={name}
              className={`chip${tag === name ? " an" : ""}`}
              onClick={() => setTag(tag === name ? null : name)}
            >
              {name} <span style={{ opacity: 0.5 }}>{n}</span>
            </button>
          ))}
        </div>
      )}

      {!laedt && !gefiltert.length && (
        <p className="lauftext" style={{ marginTop: 40 }}>
          Kein Band passt zu dieser Auswahl.
        </p>
      )}

      <Regal bretter={bretter} onOeffnen={setOffen} />

      {offen && <Detail book={offen} onSchliessen={() => setOffen(null)} />}
    </>
  );
}
